import React, { useState, useEffect, Fragment, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { Alert, Dialog, Snackbar } from "@mui/material";
import "../Candidate-Upload/candidateUpload.scss";
import TimeZone from "../../utils/all_timezones.json";
import {
  fetchAvailableSlots,
  getCandidateHiringStage,
  initiateQueueWebCall,
  initiateWebVoiceCall,
  scheduledHiringStage,
} from "../../Redux/ActionCreators/jobsAction";
import { fetchTenantBranding } from "../../Redux/ActionCreators/qualifyQuestionsAction";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Vapi from "@vapi-ai/web";
import "../Qualify-Questions/qualifying.scss";

const StageIds = {
  "293e9b95-4610-48f2-ae93-c7264cae7d6a": "Assessment",
  "ed658e02-1549-4d9e-a735-1f1c1e385f46": "Video Call",
  "fd40e3d2-38a9-4cef-937a-93ef6a42d045": "Voice Call",
};

export default function ScheduleStage() {
  const webCallRef = useRef(null);
  const endCallTimerRef = useRef(null);
  const endTimerStartedRef = useRef(false);

  const [screenLoading, setScreenLoading] = useState(false);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [scheduleDate, setScheduleDate] = useState(moment());
  const [timezone, setTimezone] = useState("");
  const [slotPopupOpen, setSlotPopupOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [showPhoneCheckModal, setShowPhoneCheckModal] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [linkExpiredMessage, setLinkExpiredMessage] = useState("Link has been expired");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [scheduleCompleted, setScheduleCompleted] = useState(false);
  const [maxScheduleDate, setMaxScheduleDate] = useState(
    moment().add(15, "days").toDate()
  );
  const [webCallStarted, setWebCallStarted] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [vapiInstance, setVapiInstance] = useState(null);
  const [webCallCompleted, setWebCallCompleted] = useState(false);
  const [webCallInitializing, setWebCallInitializing] = useState(false);
  const [queueTimerSec, setQueueTimerSec] = useState(null);
  const [queueTimerMin, setQueueTimerMin] = useState(null);
  const [queueInterval, setQueueInterval] = useState(null);
  const [queueSocket, setQueueSocket] = useState(null);
  const [queueReady, setQueueReady] = useState(false);
  const [isRetryingQueue, setIsRetryingQueue] = useState(false);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showCallOptions, setShowCallOptions] = useState(false);
  const [callType, setCallType] = useState(null);
  const [allowExitFullscreen, setAllowExitFullscreen] = useState(false);
  const [inInterviewMode, setInInterviewMode] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [webCallError, setWebCallError] = useState(false);
  const [phoneWaitMessage, setPhoneWaitMessage] = useState("");
  const [forceScheduleScreen, setForceScheduleScreen] = useState(false);
  const [showEndCallButton, setShowEndCallButton] = useState(false);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { candidateHiringStage } = useSelector((state) => state.jobs);
  const availableSlots = useSelector((state) => state.jobs.availableSlots);
  const tenantBranding = useSelector(
    (state) => state.qualifyQuestions?.tenantBranding
  );
  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);
  const logoSrc = isWhiteLabel && tenantBranding?.logo
    ? tenantBranding.logo
    : "/images/gappeoai-sm-logo.svg";

  useEffect(() => {
    const token = searchParams.get("t");
    if (!token) return;
    setBrandingLoading(true);
    dispatch(fetchTenantBranding(token)).finally(() => setBrandingLoading(false));
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty(
        "--qualify-accent-color",
        tenantBranding.colour
      );
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  useEffect(() => {
    if (callType !== "WEB_CALL" || !inInterviewMode) return;

    const blockKeys = (e) => {
      const key = e.key.toLowerCase();

      const blocked =
        key === "escape" ||
        key === "f5" ||
        (e.ctrlKey && key === "r") ||
        (e.metaKey && key === "r");

      if (blocked) {
        e.preventDefault();
        e.stopPropagation();

        setSnackbarSeverity("error");
        setSuccessMessageText(
          "You cannot exit or refresh during the interview"
        );
        setSuccessMessage(true);
      }
    };

    window.addEventListener("keydown", blockKeys, true);

    return () => {
      window.removeEventListener("keydown", blockKeys, true);
    };
  }, [webCallStarted, callType]);

  useEffect(() => {
    const fetchJob = async () => {
      if (id) {
        setScreenLoading(true);
        try {
          await dispatch(getCandidateHiringStage(id));
          setLinkExpired(false);
        } catch (error) {
          const status = error?.parsedStatus || error?.response?.status;
          console.error("Unexpected error", error);
          if (status === 403 || status === 410) {
            const detail = (error?.originalError?.response?.data || error?.response?.data)?.detail;
            const detailMessage = typeof detail === "string" ? detail : detail?.message;
            setLinkExpired(true);
            setLinkExpiredMessage(detailMessage || "Link has been expired");
          } else if (status === 400 || status === 404) {
            setLinkExpired(true);
            setLinkExpiredMessage(
              "Invalid link, please check and try again"
            );
          }
        }
        setScreenLoading(false);
      }
    };
    fetchJob();

    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matched = TimeZone.find((tz) => tz.value === detected);
    if (matched) {
      setTimezone(matched);
    }
  }, []);

  useEffect(() => {
    if (candidateHiringStage?.job_id && timezone?.value) {
      const formattedDate = moment(scheduleDate).format("YYYY-MM-DD");
      dispatch(
        fetchAvailableSlots(candidateHiringStage.job_id, formattedDate, timezone.value)
      );
    }
  }, [candidateHiringStage?.job_id, timezone]);

  useEffect(() => {
    if (candidateHiringStage?.expire_at) {
      setMaxScheduleDate(moment(candidateHiringStage.expire_at).toDate());
    }
  }, [candidateHiringStage?.expire_at]);

  useEffect(() => {
    if (
      queueTimerSec === 0 &&
      !queueReady &&
      webCallStarted &&
      !isRetryingQueue
    ) {
      setIsRetryingQueue(true);
      handleInitiateWebCall().finally(() => {
        setIsRetryingQueue(false);
      });
    }
  }, [queueTimerSec, queueReady]);

  useEffect(() => {
    if (queueReady) {
      if (queueInterval) clearInterval(queueInterval);
      setQueueTimerSec(null);

      (async () => {
        setWebCallInitializing(true);
        const callRes = await dispatch(
          initiateWebVoiceCall(jobId, candidateId, id)
        );
        startVapiCall(callRes.data.payload);
      })();
    }
  }, [queueReady]);

  useEffect(() => {
    return () => {
      if (queueInterval) clearInterval(queueInterval);
      if (queueSocket) queueSocket.close();
    };
  }, []);

  const requestMicPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    if (callType !== "WEB_CALL" || !inInterviewMode) return;

    const onFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;

      // Allow normal exit only when call has ended
      if (allowExitFullscreen) return;

      // User exited fullscreen (ESC, browser UI, etc.)
      if (!isFullscreen) {
        setTimeout(() => {
          if (
            webCallRef.current &&
            inInterviewMode &&
            !allowExitFullscreen
          ) {
            webCallRef.current.requestFullscreen().catch(() => { });
          }
        }, 150);
      }
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [inInterviewMode, callType, allowExitFullscreen]);

  const fullName = candidateHiringStage?.candidate?.name || "";
  const [firstName, lastName] = fullName.split(" ");
  const stageName = StageIds[candidateHiringStage?.hiring_stage_id] || "";
  const candidateId = candidateHiringStage?.candidate?.id || "";
  const jobId = candidateHiringStage?.job_id || "";
  const hiringStatus = candidateHiringStage?.status?.name?.toLowerCase();
  const isOngoingOrCompleted = hiringStatus === "ongoing" || hiringStatus === "completed";

  const handleSlotSelection = (id) => {
    setSelectedSlotId(id);
    setSlotPopupOpen(false);
  };

  const handleDateChange = (date) => {
    setScheduleDate(date);
    const formattedDate = moment(date).format("YYYY-MM-DD");
    dispatch(fetchAvailableSlots(candidateHiringStage?.job_id, formattedDate, timezone?.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await setLoading(true);

    let obj = {
      id: id,
      schedule_date: moment(scheduleDate).format("YYYY-MM-DD"),
      schedule_timezone: timezone ? timezone.value : "",
      slot_id: selectedSlotId,
    };
    const res = await dispatch(
      scheduledHiringStage(obj, candidateHiringStage?.job_id)
    );
    if (res === 200) {
      setSnackbarSeverity("success");
      setSuccessMessage(true);
      setSuccessMessageText("Stage scheduled and email sent successfully");
      setScheduleDate("");
      setTimezone(null);
      setScheduleCompleted(true);
    } else if (res?.success === false) {
      const message =
        res.message ||
        "Something went wrong during scheduling please contact administrator";
      if (message.toLowerCase().includes("country code")) {
        setShowPhoneCheckModal(true);
        setSuccessMessageText(message);
      } else {
        setSnackbarSeverity("error");
        setSuccessMessage(true);
        setSuccessMessageText(message);
      }
    } else {
      setSnackbarSeverity("error");
      setSuccessMessage(true);
      setSuccessMessageText(
        "Something went wrong during scheduling please contact administrator"
      );
    }
    await setLoading(false);
  };

  const snackClosed = () => {
    setSuccessMessage(false);
    setSuccessMessageText("");
  };

  const isDisabled = scheduleDate && timezone && selectedSlotId;

  const handleInitiateWebCall = async () => {
    setMicDenied(false);
    setQueueReady(false);
    setWebCallError(false);

    const micAllowed = await requestMicPermission();

    if (!micAllowed) {
      setMicDenied(true);
      setSnackbarSeverity("warning");
      setSuccessMessage(true);
      setSuccessMessageText("You have declined the microphone access");
      return;
    }

    try {
      setWebCallInitializing(true);
      const res = await dispatch(initiateQueueWebCall(jobId, candidateId, id, { type: "WEB_CALL" }));
      const waitTime = res?.data?.estimated_wait_time ?? 0;

      setWebCallInitializing(false);
      setWebCallStarted(true);

      if (waitTime > 0) {
        startQueueTimer(waitTime);
      }

      connectQueueSocket(id, "WEB_CALL");
    } catch (err) {
      console.error(err);
      setWebCallInitializing(false);
    }
  };

  const handleViaPhoneCall = async () => {
    setCallType("PHONE_CALL");
    setShowCallOptions(false);
    setQueueReady(false);
    setWebCallCompleted(false);
    setInInterviewMode(true);
    setWebCallError(false)

    try {
      setWebCallInitializing(true);

      const res = await dispatch(
        initiateQueueWebCall(jobId, candidateId, id, {
          type: "PHONE_CALL",
        })
      );

      const data = res?.data;
      const waitTimeSec = data?.estimated_wait_time ?? 0;
      const forceSchedule = data?.force_schedule;

      setWebCallInitializing(false);

      if (forceSchedule) {
        setInInterviewMode(false);
        setAllowExitFullscreen(true);
        setForceScheduleScreen(true);
        setWebCallStarted(false);
        setWebCallCompleted(true);
        return;
      }

      const waitTimeMin = Math.ceil(waitTimeSec / 60);

      let message =
        waitTimeMin < 5
          ? "Your phone call was initiated successfully. You will receive a phone call in a couple of minutes."
          : `Your phone call was initiated successfully. You will receive a phone call in ${waitTimeMin} minutes.`;

      setPhoneWaitMessage(message);
      setSnackbarSeverity("success");
      setSuccessMessageText(message);
      setSuccessMessage(true);

      if (waitTimeMin < 30) {
        setWebCallCompleted(true);
        setWebCallStarted(false);
        return;
      }
      setWebCallStarted(true);
      startQueueTimer(waitTimeSec);
      connectQueueSocket(id, "PHONE_CALL");
    } catch (err) {
      console.error(err);
      setWebCallInitializing(false);
      setSuccessMessage(true);
    }
  };

  const startQueueTimer = (seconds) => {
    clearInterval(queueInterval);

    setQueueTimerSec(seconds);
    setQueueTimerMin(Math.ceil(seconds / 60));

    const interval = setInterval(() => {
      setQueueTimerSec((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setQueueInterval(interval);
  };

  const connectQueueSocket = (hiringStageId, type) => {
    if (queueSocket) {
      queueSocket.onmessage = null;
      queueSocket.onerror = null;
      queueSocket.close();
    }

    const ws = new WebSocket(
      `${process.env.REACT_APP_WS_URL}?job_candidate_hiring_stage_id=${hiringStageId}&type=${type}`
    );

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.type === "queue_item_ready") {
          ws.close();
          if (callType === "PHONE_CALL") {
            setWebCallStarted(false);
            setWebCallCompleted(true);
          } else {
            setQueueReady(true);
          }
        }
      } catch (e) {
        console.error("WS parse error", e);
      }
    };

    ws.onerror = (e) => {
      console.error("WebSocket error", e);
    };

    setQueueSocket(ws);
  };

  const enterFullscreen = async () => {
    const el = webCallRef.current;

    if (!el) {
      return;
    }

    if (document.fullscreenElement) {
      return;
    }

    try {
      await el.requestFullscreen();
    } catch (err) {
      console.error("Fullscreen request failed:", err);
    }
  };


  const startVapiCall = (payload) => {
    const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);
    setVapiInstance(vapi);

    vapi.start(payload.assistant, payload.assistantOverrides);

    vapi.on("speech-start", () => {
      setWebCallInitializing(false);
      setWebCallStarted(true);
      setAllowExitFullscreen(false);
      setIsAssistantSpeaking(true);
      setIsUserSpeaking(false);
      if (!endTimerStartedRef.current) {
        endTimerStartedRef.current = true;
        setShowEndCallButton(false);

        endCallTimerRef.current = setTimeout(() => {
          setShowEndCallButton(true);
        }, 180000);
      }
    });

    vapi.on("assistant-speech-start", () => {
      setIsAssistantSpeaking(true);
      setIsUserSpeaking(false);
    });

    vapi.on("speech-end", () => {
      setIsAssistantSpeaking(false);
      setIsUserSpeaking(true);
    });

    vapi.on("call-end", () => {
      cleanupCall(true);
    });

    vapi.on("error", (err) => {
      setWebCallError(true);

      setWebCallStarted(false);
      setWebCallCompleted(false);
      setScheduleCompleted(false);
      setWebCallInitializing(false);
      setInInterviewMode(false);
      setAllowExitFullscreen(true);

      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => { });
      }

      setSnackbarSeverity("error");
      setSuccessMessage(true);
      setSuccessMessageText(
        "Failed to connect web call. Please try again."
      );
    });
  };

  const handleEndVoiceCall = () => {
    setAllowExitFullscreen(true);
    setInInterviewMode(false);

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => { });
    }

    if (vapiInstance) {
      vapiInstance.stop();
    }

    cleanupCall(true);
  };

  const cleanupCall = (completed = false) => {
    setAllowExitFullscreen(true);
    setWebCallStarted(false);
    setInInterviewMode(false);
    setVapiInstance(null);

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => { });
    }

    if (completed) setWebCallCompleted(true);

    if (endCallTimerRef.current) {
      clearTimeout(endCallTimerRef.current);
      endCallTimerRef.current = null;
    }
    setShowEndCallButton(false);
    endTimerStartedRef.current = false;
  };

  const handleViaWebCall = async () => {
    const allowed = await requestMicPermission();
    if (!allowed) return;

    setCallType("WEB_CALL");
    setAllowExitFullscreen(false);
    setInInterviewMode(true);
    setWebCallError(false);
    setInInterviewMode(true);

    await enterFullscreen();
    handleInitiateWebCall();
  };

  if (screenLoading || brandingLoading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Dialog
        className="schedule-loader"
        open={loading}
        onClose={() => { }}
        PaperProps={{
          sx: {
            boxShadow: "none",
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {successMessageText}
        </Alert>
      </Snackbar>
      {linkExpired ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "24px",
            color: "#ff0000",
          }}
        >
          {linkExpiredMessage}
        </div>
      ) : (
        <div className="d-flex">
          <div
            className="candidate-upload pre-screening"
            style={{ padding: "30px 0" }}
          >
            {(webCallCompleted || scheduleCompleted) && !webCallError && (
              <div
                className="thank-you-screen"
                style={{
                  minHeight: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: forceScheduleScreen ? "#FEF3C7" : "#f0fff4",
                  padding: "40px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  {!forceScheduleScreen && <img src="/images/thankyou-icon.gif" alt="" />}
                  <h2
                    style={{
                      marginBottom: "12px",
                      color: forceScheduleScreen ? "#92400E" : "#03543F",
                    }}
                  >
                    {forceScheduleScreen ? "Attention Required" : "Thank you!"}
                  </h2>
                  <p
                    style={{
                      fontSize: "18px",
                      color: forceScheduleScreen ? "#78350F" : "#065F46",
                    }}
                  >
                    {forceScheduleScreen
                      ? "Estimated wait time is too long. Please schedule your interview for later."
                      : callType === "PHONE_CALL"
                        ? phoneWaitMessage
                        : webCallCompleted
                          ? "You have successfully completed your voice interview."
                          : "Your voice interview has been scheduled successfully."}
                  </p>
                  {forceScheduleScreen ? (
                    <button
                      className="btn btn-warning mt-4"
                      onClick={() => {
                        setForceScheduleScreen(false);
                        setWebCallCompleted(false);
                        setShowScheduleForm(true);
                      }}
                    >
                      Schedule Later
                    </button>
                  ) : (
                    <p style={{ marginTop: "12px", color: "#047857" }}>
                      You may now safely close this window.
                    </p>
                  )}
                </div>
              </div>
            )}
            {!webCallStarted && !scheduleCompleted && !webCallInitializing && (!webCallCompleted || webCallError) && (
              <>
                <div className="candidate-upload-header">
                  <div className="candidate-upload-title">
                    <div className="company-logo">
                      <img src={logoSrc} alt="" width={60} height={60} />
                    </div>
                    <div className="position-details">
                      <h3>{candidateHiringStage?.job?.title}</h3>
                      <p className="position-subtitle">
                        {[
                          candidateHiringStage?.job?.company_name,
                          candidateHiringStage?.job?.job_type?.name,
                          candidateHiringStage?.job?.experience_required &&
                            `${candidateHiringStage.job.experience_required} yrs exp`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </p>
                      {candidateHiringStage?.job?.country?.name && (
                        <p className="job-qualifications">
                          <strong>Location:</strong>{" "}
                          {[candidateHiringStage?.job?.city?.name, candidateHiringStage?.job?.country?.name]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                      {candidateHiringStage?.job?.qualifications && (
                        <p className="job-qualifications">
                          <strong>Job qualifications:</strong>{" "}
                          {candidateHiringStage.job.qualifications}
                        </p>
                      )}
                      <div className="requirements">
                        {candidateHiringStage?.job?.skills
                          ?.slice(0, 2)
                          .map((skills, index) => (
                            <p className="candidate-skills" key={index}>
                              {skills}
                            </p>
                          ))}
                        {candidateHiringStage?.job?.skills?.length > 2 && (
                          <p className="candidate-skills more-skills">
                            +{candidateHiringStage?.job?.skills.length - 2} more
                            <div className="skills-popup">
                              {candidateHiringStage?.job?.skills
                                .slice(2)
                                .map((s, idx, arr) => (
                                  <span key={idx}>
                                    {s}
                                    {idx < arr.length - 1 && ", "}
                                  </span>
                                ))}
                            </div>
                          </p>
                        )}
                        {candidateHiringStage?.job?.salary && (
                          <p style={{ background: "#DEF7EC", color: "#03543F" }}>
                            {candidateHiringStage?.job?.salary}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="candidate-upload-body">
                  <form readonly>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-section">
                          <label>First Name *</label>
                          <input
                            type="text"
                            name="first_name"
                            placeholder="First Name"
                            value={firstName}
                            readonly
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-section">
                          <label>Last Name *</label>
                          <input
                            type="text"
                            name="last_name"
                            placeholder="Last Name"
                            value={lastName}
                            readonly
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-section">
                          <label>Email ID</label>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email ID"
                            value={candidateHiringStage?.candidate?.email}
                            readonly
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-section">
                          <label>Phone number</label>
                          <input
                            type="text"
                            name="phone_number"
                            placeholder="Phone Number"
                            value={
                              candidateHiringStage?.candidate?.phone_number
                            }
                            readonly
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="candidate-upload-body">
                  {!showScheduleForm && !showCallOptions && (
                    <>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            if (candidateHiringStage?.allow_phone_call) {
                              setShowCallOptions(true);
                            } else {
                              handleViaWebCall();
                            }
                          }}
                          disabled={isOngoingOrCompleted}
                        >
                          Start Now
                        </button>
                        {candidateHiringStage?.allow_phone_call && (
                          <>
                            <p className="text-center">or</p>
                            <button
                              className="btn btn-primary"
                              onClick={() => setShowScheduleForm(true)}
                              disabled={isOngoingOrCompleted}
                            >
                              Schedule Later
                            </button>
                          </>
                        )}
                      </div>
                      {isOngoingOrCompleted && (
                        <p
                          style={{
                            color: "#B91C1C",
                            marginTop: "12px",
                            fontWeight: 500,
                          }}
                        >
                          Your voice call has been {hiringStatus}. You cannot
                          take it again.
                        </p>
                      )}
                    </>
                  )}
                  {showScheduleForm && (
                    <>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "8px", cursor: 'pointer' }}
                        onClick={() => setShowScheduleForm(false)}
                      >
                        <img
                          src="/images/back-arrow-black.svg"
                          alt=""
                          style={{ cursor: "pointer" }}
                        />
                        <h3 style={{ cursor: "pointer" }}>Back</h3>
                      </div>
                      <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
                        <h3
                          style={{
                            color: "#111928",
                            marginBottom: "30px",
                          }}
                        >
                          {stageName && `Scheduling for ${stageName}`}
                        </h3>
                        <div className="form-section">
                          <label>Select Date:</label>
                          <DatePicker
                            selected={scheduleDate?.toDate?.() || scheduleDate}
                            onChange={handleDateChange}
                            dateFormat="dd-MM-yyyy"
                            minDate={new Date()}
                            maxDate={maxScheduleDate}
                            required
                            className="form-control"
                          />
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Timezone:</label>
                          <Select
                            options={TimeZone}
                            value={timezone}
                            onChange={setTimezone}
                            placeholder="Select Timezone"
                            isClearable
                          />
                        </div>
                        <div className="form-section">
                          <label>Select Time slot:</label>
                          <input
                            type="text"
                            className="form-control"
                            readOnly
                            value={
                              selectedSlotId
                                ? availableSlots.find(
                                  (s) => s.id === selectedSlotId
                                )?.display || "Selected"
                                : ""
                            }
                            placeholder="Click to select a time slot"
                            onClick={() => setSlotPopupOpen(true)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={!isDisabled || isOngoingOrCompleted}
                          className="btn btn-success"
                        >
                          Schedule
                        </button>
                        {isOngoingOrCompleted && (
                          <p
                            style={{
                              color: "#B91C1C",
                              marginTop: "12px",
                              fontWeight: 500,
                            }}
                          >
                            Your voice call has been {hiringStatus}. You cannot
                            take it again.
                          </p>
                        )}
                      </form>
                    </>
                  )}
                  {showCallOptions && !webCallStarted && (
                    <>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "8px", marginBottom: "8px", cursor: 'pointer' }}
                        onClick={() => setShowCallOptions(false)}
                      >
                        <img
                          src="/images/back-arrow-black.svg"
                          alt=""
                          style={{ cursor: "pointer" }}
                        />
                        <h3 style={{ cursor: "pointer" }}>Back</h3>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button className="btn" onClick={handleViaWebCall} disabled={isOngoingOrCompleted}>
                          Via Web Call
                        </button>
                        {candidateHiringStage?.allow_phone_call && (
                          <>
                            <p className="text-center">or</p>
                            <button className="btn" onClick={handleViaPhoneCall} disabled={isOngoingOrCompleted}>
                              Via Phone
                            </button>
                          </>
                        )}
                      </div>

                      {micDenied && (
                        <p style={{ color: "red", marginTop: "16px" }}>
                          You can take the voice interview only if you allow
                          microphone access. Please enable the microphone by
                          clicking the settings icon near the browser address
                          bar.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div ref={webCallRef} className={`webcall-fullscreen-root ${inInterviewMode && !webCallError ? "active" : ""}`} >
            {webCallInitializing && (
              <div className="loading-overlay" style={{ zIndex: 9999 }}>
                <div className="spinner-border" />
              </div>
            )}
            {!webCallCompleted && webCallStarted && (
              <div className="web-call-container">
                <div className="call-center">
                  <div className="interview-warning">
                    ⚠️ Do NOT refresh the page or press the back button during the interview
                  </div>
                  <img src={logoSrc} alt="" width={160} height={80} style={{ marginBottom: '20px' }} />
                  <div className={`call-div ${!queueReady && queueTimerSec === null ? "connecting" : ""}`}>
                    {!queueReady ? (
                      <div style={{ textAlign: "center" }}>
                        {queueTimerSec !== null ? (
                          <>
                            <h2 style={{ marginBottom: "8px" }}>
                              {Math.floor(queueTimerSec / 60)
                                .toString()
                                .padStart(2, "0")}
                              :{(queueTimerSec % 60).toString().padStart(2, "0")}
                            </h2>
                            <p style={{ color: "#000000" }}>
                              Connecting you to the interviewer…
                            </p>
                          </>
                        ) : (
                          <div className="connecting-state">
                            <p className="connecting-text">
                              Please wait, you will be connected to agent shortly
                            </p>
                            <div className="connecting-spinner" role="status">
                              <span className="visually-hidden">Connecting…</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {isAssistantSpeaking ? (
                          <img src="/images/reddot-gif-img.svg" alt="AI speaking" />
                        ) : (
                          <img src="/images/reddot-img.svg" alt="Listening" />
                        )}
                      </>
                    )}
                  </div>
                  <p>
                    Please ensure you are in a quiet environment with good
                    signal strength
                  </p>

                  <p>Please make sure you are speaking loudly.</p>
                  {showEndCallButton && (
                    <button className="btn btn-danger mt-4" onClick={handleEndVoiceCall}>
                      End Voice Call
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div
            className={`pre-screening-job-details ${showJobDetails ? "open" : ""
              }`}
          >
            <h3>{candidateHiringStage?.job?.title}</h3>
            <div
              className="d-flex justify-content-between"
              style={{ marginBottom: "32px" }}
            >
              <div>
                <h6>Company details</h6>
                <p>{candidateHiringStage?.job?.company_name}</p>
              </div>
              <div>
                <h6>Salary range</h6>
                <p>{candidateHiringStage?.job?.salary}</p>
              </div>
            </div>
            <div
              className="d-flex justify-content-between"
              style={{ marginBottom: "32px" }}
            >
              <div>
                <h6>Experience</h6>
                <p>{candidateHiringStage?.job?.experience_required}</p>
              </div>
            </div>
            <div
              className="d-flex justify-content-between"
              style={{ marginBottom: "32px" }}
            >
              <div>
                <h6>Skills</h6>
                <p>
                  {candidateHiringStage?.job?.skills?.map((skills, index) => (
                    <p key={index}>{skills},</p>
                  ))}
                </p>
              </div>
            </div>
            <div>
              <div>
                <h6>Description</h6>
                <p className="description-text">
                  {candidateHiringStage?.job?.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPhoneCheckModal && (
        <div className="upload-modal">
          <div
            className="modal-content"
            style={{ width: "500px", height: "auto" }}
          >
            <div className="modal-head">
              <h3>Phone Number Check</h3>
              <img
                src="/images/close-icon.svg"
                alt="Close"
                style={{ cursor: "pointer" }}
                onClick={() => setShowPhoneCheckModal(false)}
              />
            </div>
            <div className="modal-input" style={{ padding: "20px" }}>
              <p>{successMessageText}</p>
            </div>
            <div className="d-flex justify-content-center">
              <button
                className="upload-btn"
                onClick={() => setShowPhoneCheckModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {slotPopupOpen && (
        <div className="slot-popup-overlay">
          <div className="slot-popup-content">
            <div className="d-flex justify-content-evenly">
              <div className="d-flex flex-column align-items-center">
                <h4>Select a Time</h4>
                <p>Duration: 15 min</p>
              </div>
              <img
                className="cursor-pointer"
                src="/images/close.svg"
                alt=""
                onClick={() => setSlotPopupOpen(false)}
              />
            </div>
            <div className="slot-list">
              {availableSlots.map((slot) => (
                <button
                  key={slot.id}
                  className="slot-btn"
                  onClick={() => handleSlotSelection(slot.id)}
                >
                  {slot.display || moment(slot.time_of_day).format("h:mma")}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
